'use client';

import React from 'react';
import Link from 'next/link';
import { LogIn } from 'lucide-react';
import LoginFormComponent from '@/components/(dashboard)/auth/LoginFormComponent';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';

/** DialogContent classes for a login prompt: same background as the form, so no box-in-a-box. */
export const LOGIN_DIALOG_CLASS =
    'w-[calc(100%-2rem)] max-w-md gap-0 overflow-hidden rounded-2xl border-black/5 bg-white p-6 dark:border-white/10 dark:bg-primary-dark';

interface LoginPromptProps {
    /** Why login is needed, e.g. «برای پاسخ دادن وارد شوید». */
    reason: string;
    /** What happens after login. */
    hint?: string;
    onSuccess: () => void | Promise<void>;
}

/**
 * Body of the review / comment login dialogs (reply, like, rating, review). The form keeps its
 * own «ورود یا ثبت نام» heading; the reason is a short note above it instead of a second title.
 */
const LoginPrompt: React.FC<LoginPromptProps> = ({ reason, hint, onSuccess }) => (
    <div dir='rtl' className='w-full text-right'>
        <DialogTitle className='sr-only'>{reason}</DialogTitle>
        <DialogDescription asChild>
            <div className='mb-5 mt-6 flex items-center gap-3 rounded-xl border border-progress/15 bg-progress/5 p-3 dark:border-white/10 dark:bg-white/5'>
                <span className='flex size-9 shrink-0 items-center justify-center rounded-full bg-progress/10 text-progress dark:bg-white/10 dark:text-blue-300'>
                    <LogIn size={16} aria-hidden='true' />
                </span>
                <span className='min-w-0'>
                    <span className='block text-sm font-medium text-gray-900 dark:text-white'>
                        {reason}
                    </span>
                    {hint && (
                        <span className='mt-0.5 block text-xs leading-5 text-gray-500 dark:text-white/60'>
                            {hint}
                        </span>
                    )}
                </span>
            </div>
        </DialogDescription>
        <LoginFormComponent handleLoginSuccess={onSuccess} />
        <p className='mt-4 text-center text-[11px] leading-5 text-gray-500 dark:text-white/50'>
            ورود و ثبت‌نام شما به معنای پذیرش{' '}
            <Link
                prefetch={false}
                target='_blank'
                href='/privacy-policy'
                className='text-progress dark:text-blue-300'
            >
                قوانین
            </Link>{' '}
            خانه سرمایه است.
        </p>
    </div>
);

export default LoginPrompt;
